<template>
  <div class="migrations-tab p-6">
    <!-- Migration Description -->
    <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
      <h2 class="text-xl font-semibold text-blue-900 mb-3">
        <i class="pi pi-info-circle mr-2"></i>
        Schema Configuration Migration
      </h2>
      <div class="text-blue-800 space-y-2">
        <p>
          This migration moves schema configuration fields from root-level to a nested
          <code class="bg-blue-100 px-2 py-1 rounded">config</code> structure.
        </p>
        <p><strong>What will be migrated:</strong></p>
        <ul class="list-disc list-inside ml-4 space-y-1">
          <li><code>devTipsEnabled</code> → <code>config.devTips.enabled</code></li>
          <li>
            <code>devTipsPeriod</code> → <code>config.devTips.schedule.period</code>
          </li>
          <li>
            <code>rotatedQuestionsSchema</code> → <code>config.questionRotation.enabled</code>
          </li>
          <li><code>rotationPeriod</code> → <code>
            config.questionRotation.schedule.period
           </code></li>
          <li><code>levelSystemEnabled</code> → <code>config.levelSystem.enabled</code></li>
          <li><code>xpPerCompletion</code> → <code>config.levelSystem.xpPerCompletion</code></li>
          <li><code>isFirstLoginTask</code> → <code>config.firstLoginTask.enabled</code></li>
          <li><code>canCreateExternalEvaluation</code>
             → <code>config.externalEvaluation.canCreate</code></li>
          <li><code>externalEvaluationSchemaId</code>
             → <code>config.externalEvaluation.schemaId</code></li>
          <li><code>canBeCreatedByAdmin</code>
             → <code>config.adminCreation.canBeCreatedByAdmin</code></li>
          <li>
            Tag groups, workshop series IDs, external evaluation fields, and
            <strong>tenant roles from legacy config</strong> (admin / company superuser lists →
            <code>User.roles</code>, baseline role enum values)
          </li>
        </ul>
        <p class="mt-3 text-sm">
          <i class="pi pi-exclamation-triangle mr-1"></i>
          <strong>Note:</strong> This migration is idempotent.
          Schemas that have already been migrated will be skipped.
        </p>
        <p class="mt-2 text-sm text-blue-700">
          <i class="pi pi-info-circle mr-1"></i>
          <strong>Dry Run:</strong> Previews what would be migrated without changing the database.
          Review the results, then click &quot;Run Migration&quot; to apply.
        </p>
      </div>
    </div>

    <!-- Current Tenant Info -->
    <div class="bg-white border border-gray-200 rounded-lg p-6 mb-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Migration Scope</h3>
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-center">
          <i class="pi pi-info-circle text-blue-600 mr-3 text-xl"></i>
          <div>
            <p class="text-blue-900 font-medium">
              This migration will only affect the current tenant.
            </p>
            <p v-if="currentTenantSlug" class="text-blue-700 text-sm mt-1">
              Current tenant: <strong>{{ currentTenantSlug }}</strong>
            </p>
            <p v-else class="text-blue-700 text-sm mt-1">
              Single-tenant mode (no tenant context)
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex space-x-3 mb-6">
      <Button
        label="Run Dry Run"
        icon="pi pi-search"
        :loading="loading"
        @click="handleDryRun"
        class="p-button-info"
      />
      <Button
        label="Run Migration"
        icon="pi pi-check"
        :loading="loading"
        :disabled="!hasDryRunResults"
        @click="handleMigration"
        class="p-button-success"
      />
      <Button
        v-if="hasDryRunResults || hasMigrationResults"
        label="Clear Results"
        icon="pi pi-times"
        :disabled="loading"
        @click="clearResultsHandler"
        class="p-button-secondary"
      />
    </div>

    <!-- Dry Run Results -->
    <div v-if="hasDryRunResults" class="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
      <h3 class="text-lg font-semibold text-yellow-900 mb-4">
        <i class="pi pi-info-circle mr-2"></i>
        Dry Run Results
      </h3>
      <div class="space-y-3">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Total Schemas</div>
            <div class="text-2xl font-bold text-gray-800">{{ dryRunResults.total || 0 }}</div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">To Migrate</div>
            <div class="text-2xl font-bold text-blue-600">{{ dryRunResults.toMigrate || 0 }}</div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Already Migrated</div>
            <div class="text-2xl font-bold text-green-600">
              {{ dryRunResults.alreadyMigrated || 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Errors</div>
            <div class="text-2xl font-bold text-red-600">
              {{ dryRunResults.errors?.length || 0 }}
            </div>
          </div>
        </div>
        <div v-if="dryRunResults.errors && dryRunResults.errors.length > 0" class="mt-4">
          <h4 class="font-semibold text-red-800 mb-2">Errors:</h4>
          <ul class="list-disc list-inside space-y-1 text-red-700">
            <li v-for="(error, index) in dryRunResults.errors" :key="index">
              {{ error }}
            </li>
          </ul>
        </div>
        <div v-if="dryRunResults.tenantRoles" class="mt-4 p-4 bg-white rounded border border-amber-200">
          <h4 class="font-semibold text-amber-900 mb-2">Tenant roles (legacy config)</h4>
          <p class="text-sm text-gray-700 mb-2">Preview from the same run:</p>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div v-for="(row, i) in tenantRolesRows(dryRunResults.tenantRoles)" :key="'tr-dr-' + i">
              <div class="text-gray-500 text-xs">{{ row.label }}</div>
              <div class="font-semibold text-gray-900">{{ row.value }}</div>
            </div>
          </div>
        </div>
        <div class="mt-4 p-4 bg-blue-100 rounded">
          <p class="text-blue-800">
            <i class="pi pi-check-circle mr-2"></i>
            Dry run completed successfully. Review the results above and click
            "Run Migration" to proceed.
          </p>
        </div>
      </div>
    </div>

    <!-- Migration Results -->
    <div v-if="hasMigrationResults" class="bg-green-50 border border-green-200 rounded-lg p-6">
      <h3 class="text-lg font-semibold text-green-900 mb-4">
        <i class="pi pi-check-circle mr-2"></i>
        Migration Results
      </h3>
      <div class="space-y-3">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Total Processed</div>
            <div class="text-2xl font-bold text-gray-800">{{ migrationResults.total || 0 }}</div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Migrated</div>
            <div class="text-2xl font-bold text-green-600">
              {{ migrationResults.migrated || 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Skipped</div>
            <div class="text-2xl font-bold text-yellow-600">
              {{ migrationResults.skipped || 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Errors</div>
            <div class="text-2xl font-bold text-red-600">
              {{ migrationResults.errors?.length || 0 }}
            </div>
          </div>
        </div>
        <div v-if="migrationResults.errors && migrationResults.errors.length > 0" class="mt-4">
          <h4 class="font-semibold text-red-800 mb-2">Errors:</h4>
          <ul class="list-disc list-inside space-y-1 text-red-700">
            <li v-for="(error, index) in migrationResults.errors" :key="index">
              {{ typeof error === 'object' ? JSON.stringify(error) : error }}
            </li>
          </ul>
        </div>
        <div v-if="migrationResults.tenantRoles" class="mt-4 p-4 bg-white rounded border border-amber-200">
          <h4 class="font-semibold text-amber-900 mb-2">Tenant roles (legacy config)</h4>
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div v-for="(row, i) in tenantRolesRows(migrationResults.tenantRoles)" :key="'tr-mr-' + i">
              <div class="text-gray-500 text-xs">{{ row.label }}</div>
              <div class="font-semibold text-gray-900">{{ row.value }}</div>
            </div>
          </div>
        </div>
        <div class="mt-4 p-4 bg-green-100 rounded">
          <p class="text-green-800">
            <i class="pi pi-check-circle mr-2"></i>
            Migration completed successfully!
          </p>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-6 mt-6">
      <h3 class="text-lg font-semibold text-red-900 mb-2">
        <i class="pi pi-exclamation-triangle mr-2"></i>
        Error
      </h3>
      <p class="text-red-800">{{ error }}</p>
    </div>

    <!-- Tenant roles migration (standalone; same logic is also included in Schema Configuration Migration above) -->
    <div class="bg-white border border-gray-200 rounded-lg p-6 mt-8 mb-6">
      <h2 class="text-xl font-semibold text-gray-800 mb-2">
        <i class="pi pi-users mr-2"></i>
        Tenant roles migration (legacy config)
      </h2>
      <p class="text-gray-600 mb-4">
        Copies <code class="bg-gray-100 px-1 rounded">admin</code> and
        <code class="bg-gray-100 px-1 rounded">companySuperuser</code> onto
        <code class="bg-gray-100 px-1 rounded">User.roles</code> from legacy tenant configuration lists
        (<code class="bg-gray-100 px-1 rounded">admin_emails</code>,
        <code class="bg-gray-100 px-1 rounded">admin_IDs</code>,
        <code class="bg-gray-100 px-1 rounded">company_superuser_emails</code>,
        <code class="bg-gray-100 px-1 rounded">company_superuser_IDs</code>),
        and ensures baseline role enum values exist. Idempotent.
      </p>
      <div class="flex space-x-3 mb-6">
        <Button
          label="Dry run"
          icon="pi pi-search"
          :loading="tenantRolesLoading"
          @click="handleTenantRolesDryRun"
          class="p-button-info"
        />
        <Button
          label="Run migration"
          icon="pi pi-check"
          :loading="tenantRolesLoading"
          :disabled="!hasTenantRolesDryRunResults"
          @click="handleTenantRolesMigration"
          class="p-button-success"
        />
        <Button
          v-if="hasTenantRolesResults"
          label="Clear results"
          icon="pi pi-times"
          :disabled="tenantRolesLoading"
          @click="clearTenantRolesResultsHandler"
          class="p-button-secondary"
        />
      </div>
      <div v-if="tenantRolesError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <h4 class="font-semibold text-red-800 mb-1">Error</h4>
        <p class="text-red-700">{{ tenantRolesError }}</p>
      </div>
      <div v-if="hasTenantRolesResults" class="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Results</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
          <div v-for="(row, i) in tenantRolesRows(tenantRolesStandaloneReport)" :key="'tr-st-' + i">
            <div class="text-gray-500 text-xs">{{ row.label }}</div>
            <div class="font-semibold text-gray-900">{{ row.value }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dev Tip Dimension Rotation Migration -->
    <div class="bg-white border border-gray-200 rounded-lg p-6 mt-8 mb-6">
      <h2 class="text-xl font-semibold text-gray-800 mb-2">
        <i class="pi pi-list mr-2"></i>
        Dev Tip Dimension Rotation Migration
      </h2>
      <p class="text-gray-600 mb-4">
        Populates <code class="bg-gray-100 px-1 rounded">devTipDimensionRotation</code> for users who are missing it.
        For each such user, finds schemas with dev tips enabled and builds the dimension list from the
        <strong>latest submitted assessment</strong> for that schema (dimensions sorted by score ascending).
        If a user has no submitted assessment for a schema, that schema is skipped for that user.
      </p>
      <div class="flex space-x-3 mb-6">
        <Button
          label="Dry Run"
          icon="pi pi-search"
          :loading="devTipRotationLoading"
          @click="handleDevTipRotationDryRun"
          class="p-button-info"
        />
        <Button
          label="Run Migration"
          icon="pi pi-check"
          :loading="devTipRotationLoading"
          :disabled="!hasDevTipRotationDryRunResults"
          @click="handleDevTipRotationMigration"
          class="p-button-success"
        />
        <Button
          v-if="hasDevTipRotationResults"
          label="Clear Results"
          icon="pi pi-times"
          :disabled="devTipRotationLoading"
          @click="clearDevTipRotationResultsHandler"
          class="p-button-secondary"
        />
      </div>

      <div v-if="devTipRotationError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <h4 class="font-semibold text-red-800 mb-1">Error</h4>
        <p class="text-red-700">{{ devTipRotationError }}</p>
      </div>

      <div v-if="hasDevTipRotationResults" class="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Results</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Users Missing Property</div>
            <div class="text-2xl font-bold text-gray-800">
              {{ devTipRotationResultsSummary.totalUsersMissing ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Users Migrated</div>
            <div class="text-2xl font-bold text-green-600">
              {{ devTipRotationResultsSummary.usersMigrated ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Rotation Entries Added</div>
            <div class="text-2xl font-bold text-blue-600">
              {{ devTipRotationResultsSummary.rotationEntriesAdded ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Errors</div>
            <div class="text-2xl font-bold text-red-600">
              {{ devTipRotationResultsSummary.errorsCount ?? 0 }}
            </div>
          </div>
        </div>
        <div
          v-if="devTipRotationResultsSummary.errorsCount > 0 && devTipRotationResultsSummary.errorDetails?.length"
          class="mt-4"
        >
          <h4 class="font-semibold text-red-800 mb-2">Error details:</h4>
          <ul class="list-disc list-inside space-y-1 text-red-700">
            <li
              v-for="(err, index) in devTipRotationResultsSummary.errorDetails"
              :key="index"
            >
              {{ typeof err === 'object' && err?.error ? err.error : (typeof err === 'object' ? JSON.stringify(err) : err) }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Features collection cleanup migration -->
    <div class="bg-white border border-gray-200 rounded-lg p-6 mt-8 mb-6">
      <h2 class="text-xl font-semibold text-gray-800 mb-2">
        <i class="pi pi-sitemap mr-2"></i>
        Features collection cleanup
      </h2>
      <p class="text-gray-600 mb-4">
        Scans every feature row for this tenant: removes legacy fields not on the current schema,
        fills missing fields with defaults, dedupes by legacy <code class="bg-gray-100 px-1 rounded">type</code>
        (keeps the first row per type by <code class="bg-gray-100 px-1 rounded">_id</code>, deletes later duplicates).
        Administrative edits afterward handle menus as usual—this step is cleanup only.
        Run a dry run first, then apply for the current tenant.
      </p>
      <div class="flex space-x-3 mb-6">
        <Button
          label="Dry Run"
          icon="pi pi-search"
          :loading="featureMigrateLoading"
          @click="handleFeaturesMigrateDryRun"
          class="p-button-info"
        />
        <Button
          label="Run Migration"
          icon="pi pi-check"
          :loading="featureMigrateLoading"
          :disabled="!hasFeatureMigrateDryRunResults"
          @click="handleFeaturesMigrateConfirm"
          class="p-button-success"
        />
        <Button
          v-if="hasFeatureMigrateResults"
          label="Clear Results"
          icon="pi pi-times"
          :disabled="featureMigrateLoading"
          @click="clearFeatureMigrateResultsHandler"
          class="p-button-secondary"
        />
      </div>

      <div v-if="featureMigrateError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <h4 class="font-semibold text-red-800 mb-1">Error</h4>
        <p class="text-red-700">{{ featureMigrateError }}</p>
      </div>

      <div v-if="hasFeatureMigrateResults" class="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Results</h3>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Total rows</div>
            <div class="text-2xl font-bold text-gray-800">
              {{ featureMigrateResultsSummary.totalRows ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Duplicate type rows</div>
            <div class="text-2xl font-bold text-blue-600">
              {{ featureMigrateResultsSummary.duplicateRowsRemoved ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Rows normalized</div>
            <div class="text-2xl font-bold text-green-600">
              {{ featureMigrateResultsSummary.rowsNormalized ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Broken parent refs cleared</div>
            <div class="text-2xl font-bold text-orange-600">
              {{ featureMigrateResultsSummary.orphanRefsFixed ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Routes updated</div>
            <div class="text-2xl font-bold text-indigo-600">
              {{ featureMigrateResultsSummary.routesUpdated ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Roles defaulted</div>
            <div class="text-2xl font-bold text-indigo-600">
              {{ featureMigrateResultsSummary.rolesDefaulted ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Docs scrubbed (apply)</div>
            <div class="text-2xl font-bold text-gray-800">
              {{ featureMigrateResultsSummary.legacyFieldsStrippedFromDocs ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Catalog features created</div>
            <div class="text-2xl font-bold text-teal-600">
              {{ featureMigrateResultsSummary.catalogFeaturesCreated ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Overlays created</div>
            <div class="text-2xl font-bold text-teal-600">
              {{ featureMigrateResultsSummary.overlayFeaturesCreated ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Errors</div>
            <div class="text-2xl font-bold text-red-600">
              {{ featureMigrateResultsSummary.errorsCount ?? 0 }}
            </div>
          </div>
        </div>
        <div
          v-if="featureMigrateResultsSummary.missingCatalogKeys?.length"
          class="mb-4 rounded border border-amber-200 bg-amber-50 p-4 text-amber-900"
        >
          <h4 class="mb-1 font-semibold">Missing catalog keys (dry run / apply)</h4>
          <p class="text-sm">
            {{ featureMigrateResultsSummary.missingCatalogKeys.join(', ') }}
          </p>
        </div>
        <div
          v-if="featureMigrateResultsSummary.errorsCount > 0 && featureMigrateResultsSummary.errors?.length"
          class="mt-4"
        >
          <h4 class="font-semibold text-red-800 mb-2">Error messages:</h4>
          <ul class="list-disc list-inside space-y-1 text-red-700">
            <li v-for="(err, index) in featureMigrateResultsSummary.errors" :key="index">
              {{ err }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- TEST ONLY: Dev Tip Schedule Preview -->
    <div class="bg-amber-50 border border-amber-200 rounded-lg p-6 mt-8 mb-6">
      <h2 class="text-xl font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <i class="pi pi-calendar mr-2"></i>
        Preview Dev Tip Schedule (Parallel / Alternate)
        <span class="text-xs font-normal px-2 py-0.5 rounded bg-amber-200 text-amber-900">TEST ONLY – remove when no longer needed</span>
      </h2>
      <p class="text-gray-600 mb-4">
        Shows which schemas have dev tips enabled and the order in which dev tips would be sent for the next up to 40 occurrences.
        Select a user to see dimensions as per their devTipDimensionRotation; without a user, the dimension column shows "—".
      </p>
      <div class="flex flex-wrap items-end gap-3 mb-4">
        <Button
          label="Preview Dev Tip Schedule"
          icon="pi pi-eye"
          :loading="devTipSchedulePreviewLoading"
          @click="handleDevTipSchedulePreview"
          class="p-button-warning"
        />
        <div v-if="devTipSchedulePreview?.activeUsers?.length" class="flex items-center gap-2">
          <label for="dev-tip-preview-user" class="text-sm font-medium text-gray-700">User (for dimension column):</label>
          <select
            id="dev-tip-preview-user"
            v-model="devTipSchedulePreviewUserId"
            class="rounded border border-gray-300 px-3 py-1.5 text-sm min-w-[200px]"
            @change="handleDevTipPreviewUserChange"
          >
            <option value="">— No user —</option>
            <option
              v-for="u in devTipSchedulePreview.activeUsers"
              :key="u.id"
              :value="u.id"
            >
              {{ u.name || u.email || u.id }}{{ u.email ? ` (${u.email})` : '' }}
            </option>
          </select>
        </div>
      </div>
      <div v-if="devTipSchedulePreviewError" class="bg-red-50 border border-red-200 rounded p-3 mb-4">
        <p class="text-red-700 text-sm">{{ devTipSchedulePreviewError }}</p>
      </div>
      <div v-if="devTipSchedulePreview" class="space-y-4">
        <p class="text-sm text-gray-600">
          <strong>Delivery mode:</strong> {{ devTipSchedulePreview.deliveryMode ?? '—' }}
        </p>
        <div v-if="devTipSchedulePreview.schemas?.length" class="overflow-x-auto">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Schemas with dev tips</h3>
          <table class="w-full text-sm border border-gray-200 rounded">
            <thead>
              <tr class="bg-gray-100">
                <th class="text-left p-2">Schema name</th>
                <th class="text-left p-2">Frequency</th>
                <th class="text-left p-2">Hour (UTC)</th>
                <th class="text-left p-2">Day (weekly)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(s, i) in devTipSchedulePreview.schemas" :key="i" class="border-t border-gray-200">
                <td class="p-2">{{ s.schemaName }}</td>
                <td class="p-2">{{ s.frequency }}</td>
                <td class="p-2">{{ s.hour }}</td>
                <td class="p-2">{{ s.dayOfWeek != null ? ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][s.dayOfWeek] : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="devTipSchedulePreview.occurrences?.length" class="overflow-x-auto">
          <h3 class="text-sm font-semibold text-gray-700 mb-2">Next occurrences (max 40)</h3>
          <table class="w-full text-sm border border-gray-200 rounded">
            <thead>
              <tr class="bg-gray-100">
                <th class="text-left p-2">#</th>
                <th class="text-left p-2">Schema name</th>
                <th class="text-left p-2">Dimension</th>
                <th class="text-left p-2">Frequency</th>
                <th class="text-left p-2">Day</th>
                <th class="text-left p-2">Time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="occ in devTipSchedulePreview.occurrences" :key="occ.index" class="border-t border-gray-200">
                <td class="p-2">{{ occ.index }}</td>
                <td class="p-2">{{ occ.schemaName }}</td>
                <td class="p-2">{{ occ.dimensionName ?? '—' }}</td>
                <td class="p-2">{{ occ.frequency }}</td>
                <td class="p-2">{{ occ.day }}</td>
                <td class="p-2">{{ occ.time }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else-if="devTipSchedulePreview.deliveryMode != null" class="text-sm text-gray-500">
          No occurrences in the scanned window (or no schemas with dev tips).
        </p>
      </div>
    </div>

    <!-- Current Level Migration (separate section) -->
    <div class="bg-white border border-gray-200 rounded-lg p-6 mt-8 mb-6">
      <h2 class="text-xl font-semibold text-gray-800 mb-2">
        <i class="pi pi-user-edit mr-2"></i>
        Current Level Migration
      </h2>
      <p class="text-gray-600 mb-4">
        Copies <code class="bg-gray-100 px-1 rounded">currentRewardStatus.currentLevel</code> to top-level
        <code class="bg-gray-100 px-1 rounded">currentLevel</code> for users where currentLevel is missing, null, or 0 (per tenant).
      </p>
      <div class="flex space-x-3 mb-6">
        <Button
          label="Run Current Level Dry Run"
          icon="pi pi-search"
          :loading="currentLevelLoading"
          @click="handleCurrentLevelDryRun"
          class="p-button-info"
        />
        <Button
          label="Run Current Level Migration"
          icon="pi pi-check"
          :loading="currentLevelLoading"
          @click="handleCurrentLevelMigration"
          class="p-button-success"
        />
        <Button
          v-if="hasCurrentLevelResults"
          label="Clear Results"
          icon="pi pi-times"
          :disabled="currentLevelLoading"
          @click="clearCurrentLevelResultsHandler"
          class="p-button-secondary"
        />
      </div>

      <div v-if="currentLevelError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <h4 class="font-semibold text-red-800 mb-1">Error</h4>
        <p class="text-red-700">{{ currentLevelError }}</p>
      </div>

      <div v-if="hasCurrentLevelResults" class="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Results</h3>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Total Users</div>
            <div class="text-2xl font-bold text-gray-800">
              {{ currentLevelResultsSummary.totalUsers ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Users Migrated</div>
            <div class="text-2xl font-bold text-green-600">
              {{ currentLevelResultsSummary.migrated ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Skipped</div>
            <div class="text-2xl font-bold text-yellow-600">
              {{ currentLevelResultsSummary.skipped ?? 0 }}
            </div>
          </div>
          <div class="bg-white rounded p-3">
            <div class="text-sm text-gray-600">Errors</div>
            <div class="text-2xl font-bold text-red-600">
              {{ currentLevelResultsSummary.errorsCount ?? 0 }}
            </div>
          </div>
        </div>
        <div v-if="currentLevelResultsSummary.errorsCount > 0 && currentLevelResultsSummary.errorDetails?.length" class="mt-4">
          <h4 class="font-semibold text-red-800 mb-2">Error details:</h4>
          <ul class="list-disc list-inside space-y-1 text-red-700">
            <li v-for="(err, index) in currentLevelResultsSummary.errorDetails" :key="index">
              {{ typeof err === 'object' && err?.error ? err.error : (typeof err === 'object' ? JSON.stringify(err) : err) }}
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- PrimeVue Components: group so only this dialog receives
      migration confirm (avoids duplicate with App.vue) -->
    <ConfirmDialog group="migration" />
    <ConfirmDialog group="featureMigrate" />
    <Toast />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Toast from 'primevue/toast';
import { getTenantFromHostname } from '@/services/tenantValidation';
import migrationService from '@/services/migrationService';

export default {
  name: 'MigrationsTab',
  components: {
    Button,
    ConfirmDialog,
    Toast,
  },
  data() {
    return {
      currentTenantSlug: null,
      migrationAcceptHandled: false, // guard: only run migration once even if confirm fires twice
      featureMigrateAcceptHandled: false,
      // TEST ONLY: dev tip schedule preview
      devTipSchedulePreviewLoading: false,
      devTipSchedulePreviewError: null,
      devTipSchedulePreview: null,
      devTipSchedulePreviewUserId: '',
    };
  },
  mounted() {
    // Get current tenant slug from hostname
    this.currentTenantSlug = getTenantFromHostname();
  },
  computed: {
    ...mapGetters('migrations', [
      'isLoading',
      'error',
      'dryRunResults',
      'migrationResults',
      'hasDryRunResults',
      'hasMigrationResults',
      'isCurrentLevelLoading',
      'currentLevelError',
      'currentLevelDryRunResults',
      'currentLevelMigrationResults',
      'hasCurrentLevelResults',
      'isDevTipRotationLoading',
      'devTipRotationError',
      'devTipRotationDryRunResults',
      'devTipRotationMigrationResults',
      'hasDevTipRotationResults',
      'featureMigrateLoading',
      'featureMigrateError',
      'featureMigrateDryRunResults',
      'featureMigrateApplyResults',
      'hasFeatureMigrateDryRunResults',
      'hasFeatureMigrateResults',
      'isTenantRolesLoading',
      'tenantRolesError',
      'tenantRolesDryRunResults',
      'tenantRolesMigrationResults',
      'hasTenantRolesResults',
    ]),
    // Alias so template can use {{ loading }}
    loading() {
      return this.isLoading;
    },
    currentLevelLoading() {
      return this.isCurrentLevelLoading;
    },
    devTipRotationLoading() {
      return this.isDevTipRotationLoading;
    },
    featureMigrateResultsSummary() {
      const r = this.featureMigrateApplyResults || this.featureMigrateDryRunResults;
      if (!r) return {};
      const errors = Array.isArray(r.errors) ? r.errors : [];
      return {
        totalRows: r.totalRows ?? r.legacyParentsFound ?? 0,
        duplicateRowsRemoved: r.duplicateRowsDeleted ?? r.duplicateRowsByType ?? 0,
        rowsNormalized: r.rowsNormalized ?? 0,
        orphanRefsFixed: r.orphanParentRefsCleared ?? r.orphanParentRefsToClear ?? 0,
        routesUpdated: r.routesUpdated ?? 0,
        rolesDefaulted: r.rolesDefaulted ?? 0,
        overlayFeaturesCreated: r.overlayFeaturesCreated ?? 0,
        catalogFeaturesCreated: r.catalogFeaturesCreated ?? 0,
        missingCatalogKeys: Array.isArray(r.missingCatalogKeys) ? r.missingCatalogKeys : [],
        legacyFieldsStrippedFromDocs: r.legacyFieldsStrippedFromDocs,
        errorsCount: errors.length,
        errors,
      };
    },
    tenantRolesLoading() {
      return this.isTenantRolesLoading;
    },
    hasTenantRolesDryRunResults() {
      return !!this.tenantRolesDryRunResults;
    },
    tenantRolesStandaloneReport() {
      return this.tenantRolesMigrationResults || this.tenantRolesDryRunResults || null;
    },
    hasDevTipRotationDryRunResults() {
      return !!this.devTipRotationDryRunResults;
    },
    // Prefer migration results; fall back to dry run for display
    devTipRotationResultsSummary() {
      const r = this.devTipRotationMigrationResults || this.devTipRotationDryRunResults;
      if (!r) return {};
      const errorDetails = Array.isArray(r.errorDetails) ? r.errorDetails : [];
      const errorsCount = typeof r.errors === 'number' ? r.errors : errorDetails.length;
      return {
        totalUsersMissing: r.totalUsersMissing ?? 0,
        usersMigrated: r.usersMigrated ?? 0,
        rotationEntriesAdded: r.rotationEntriesAdded ?? 0,
        errorsCount,
        errorDetails,
      };
    },
    // Prefer migration results; fall back to dry run for display
    currentLevelResultsSummary() {
      const r = this.currentLevelMigrationResults || this.currentLevelDryRunResults;
      if (!r) return {};
      const errorDetails = Array.isArray(r.errorDetails) ? r.errorDetails : [];
      const errorsCount = typeof r.errors === 'number' ? r.errors : errorDetails.length;
      return {
        totalUsers: r.totalUsers ?? 0,
        migrated: r.migrated ?? 0,
        skipped: r.skipped ?? 0,
        errorsCount,
        errorDetails,
      };
    },
  },
  methods: {
    ...mapActions('migrations', [
      'runDryRun',
      'runMigration',
      'clearResults',
      'runCurrentLevelDryRun',
      'runCurrentLevelMigration',
      'clearCurrentLevelResults',
      'runDevTipDimensionRotationDryRun',
      'runDevTipDimensionRotationMigration',
      'clearDevTipDimensionRotationResults',
      'runFeaturesMigrateDryRun',
      'runFeaturesMigrate',
      'clearFeatureMigrateResults',
      'runTenantRolesDryRun',
      'runTenantRolesMigration',
      'clearTenantRolesResults',
    ]),
    tenantRolesRows(r) {
      if (!r || typeof r !== 'object') return [];
      const enumAdded = Array.isArray(r.enumValuesAdded) ? r.enumValuesAdded : [];
      return [
        { label: 'Dry run', value: r.dryRun ? 'Yes' : 'No' },
        {
          label: 'Admin config entries (emails + ids)',
          value: (r.adminEmailsProcessed ?? 0) + (r.adminIdsProcessed ?? 0),
        },
        {
          label: 'Superuser config entries (emails + ids)',
          value: (r.superuserEmailsProcessed ?? 0) + (r.superuserIdsProcessed ?? 0),
        },
        { label: 'Users gaining admin role', value: r.usersWouldUpdateAdmin ?? 0 },
        { label: 'Users gaining company superuser role', value: r.usersWouldUpdateSuperuser ?? 0 },
        {
          label: 'Enum values added',
          value: enumAdded.length ? enumAdded.join(', ') : '—',
        },
      ];
    },
    async handleDryRun() {
      try {
        // Don't pass tenantSlug - let backend use current tenant context
        await this.runDryRun(null);
        this.$toast.add({
          severity: 'success',
          summary: 'Dry Run Completed',
          detail: 'Dry run completed successfully. Review the results.',
          life: 5000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Dry Run Failed',
          detail: error.message || 'Failed to run dry-run migration',
          life: 5000,
        });
      }
    },
    handleMigration() {
      if (!this.hasDryRunResults) {
        this.$toast.add({
          severity: 'warn',
          summary: 'No Dry Run',
          detail: 'Please run a dry run first to preview the migration',
          life: 3000,
        });
        return;
      }

      this.$confirm.require({
        group: 'migration',
        message: 'Are you sure you want to proceed with the migration? This action cannot be undone.',
        header: 'Confirm Migration',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
          // Guard: ConfirmationService can trigger multiple ConfirmDialog instances
          // (e.g. App.vue + this tab), so accept may run twice. Run migration only once.
          if (this.migrationAcceptHandled) return;
          this.migrationAcceptHandled = true;
          this.runMigration(null)
            .then(() => {
              this.$toast.add({
                severity: 'success',
                summary: 'Migration Completed',
                detail: 'Migration completed successfully!',
                life: 5000,
              });
            })
            .catch((error) => {
              this.$toast.add({
                severity: 'error',
                summary: 'Migration Failed',
                detail: error?.message || 'Failed to run migration',
                life: 5000,
              });
            })
            .finally(() => {
              this.migrationAcceptHandled = false;
            });
        },
      });
    },
    clearResultsHandler() {
      this.clearResults();
      this.$toast.add({
        severity: 'info',
        summary: 'Results Cleared',
        detail: 'Migration results have been cleared',
        life: 3000,
      });
    },
    async handleTenantRolesDryRun() {
      try {
        await this.runTenantRolesDryRun();
        this.$toast.add({
          severity: 'success',
          summary: 'Tenant roles dry run',
          detail: 'Preview completed. Review the results below.',
          life: 5000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Tenant roles dry run failed',
          detail: error?.message || 'Request failed',
          life: 5000,
        });
      }
    },
    handleTenantRolesMigration() {
      if (!this.hasTenantRolesDryRunResults) {
        this.$toast.add({
          severity: 'warn',
          summary: 'No dry run',
          detail: 'Run a dry run first to preview tenant roles migration.',
          life: 4000,
        });
        return;
      }
      this.$confirm.require({
        group: 'migration',
        message: 'Apply tenant roles migration for this tenant? This updates User.roles from legacy config lists.',
        header: 'Confirm tenant roles migration',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
          this.runTenantRolesMigration()
            .then(() => {
              this.$toast.add({
                severity: 'success',
                summary: 'Tenant roles migration',
                detail: 'Migration completed.',
                life: 5000,
              });
            })
            .catch((error) => {
              this.$toast.add({
                severity: 'error',
                summary: 'Tenant roles migration failed',
                detail: error?.message || 'Request failed',
                life: 5000,
              });
            });
        },
      });
    },
    clearTenantRolesResultsHandler() {
      this.clearTenantRolesResults();
      this.$toast.add({
        severity: 'info',
        summary: 'Results cleared',
        detail: 'Tenant roles migration results cleared.',
        life: 3000,
      });
    },
    async handleCurrentLevelDryRun() {
      try {
        await this.runCurrentLevelDryRun(null);
        this.$toast.add({
          severity: 'success',
          summary: 'Current Level Dry Run Completed',
          detail: 'Dry run completed. Review the results below.',
          life: 5000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Current Level Dry Run Failed',
          detail: error?.message || 'Failed to run current level dry run',
          life: 5000,
        });
      }
    },
    handleCurrentLevelMigration() {
      this.runCurrentLevelMigration(null)
        .then(() => {
          this.$toast.add({
            severity: 'success',
            summary: 'Current Level Migration Completed',
            detail: 'Users migrated successfully.',
            life: 5000,
          });
        })
        .catch((error) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Current Level Migration Failed',
            detail: error?.message || 'Failed to run current level migration',
            life: 5000,
          });
        });
    },
    clearCurrentLevelResultsHandler() {
      this.clearCurrentLevelResults();
      this.$toast.add({
        severity: 'info',
        summary: 'Results Cleared',
        detail: 'Current level migration results have been cleared',
        life: 3000,
      });
    },
    async handleDevTipRotationDryRun() {
      try {
        await this.runDevTipDimensionRotationDryRun(null);
        this.$toast.add({
          severity: 'success',
          summary: 'Dry Run Completed',
          detail: 'Dev tip dimension rotation dry run completed. Review the results below.',
          life: 5000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Dry Run Failed',
          detail: error?.message || 'Failed to run dev tip dimension rotation dry run',
          life: 5000,
        });
      }
    },
    handleDevTipRotationMigration() {
      if (!this.hasDevTipRotationDryRunResults) {
        this.$toast.add({
          severity: 'warn',
          summary: 'No Dry Run',
          detail: 'Please run a dry run first to preview the migration',
          life: 3000,
        });
        return;
      }
      this.runDevTipDimensionRotationMigration(null)
        .then(() => {
          this.$toast.add({
            severity: 'success',
            summary: 'Migration Completed',
            detail: 'Dev tip dimension rotation migration completed.',
            life: 5000,
          });
        })
        .catch((error) => {
          this.$toast.add({
            severity: 'error',
            summary: 'Migration Failed',
            detail: error?.message || 'Failed to run dev tip dimension rotation migration',
            life: 5000,
          });
        });
    },
    clearDevTipRotationResultsHandler() {
      this.clearDevTipDimensionRotationResults();
      this.$toast.add({
        severity: 'info',
        summary: 'Results Cleared',
        detail: 'Dev tip dimension rotation results have been cleared',
        life: 3000,
      });
    },
    async handleFeaturesMigrateDryRun() {
      try {
        await this.runFeaturesMigrateDryRun();
        this.$toast.add({
          severity: 'success',
          summary: 'Dry run completed',
          detail:
            'Review total rows, duplicate types, and normalization counts. Apply only when the preview looks right.',
          life: 5000,
        });
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Dry run failed',
          detail: error?.message || 'Failed to run features migration dry run',
          life: 5000,
        });
      }
    },
    handleFeaturesMigrateConfirm() {
      if (!this.hasFeatureMigrateDryRunResults) {
        this.$toast.add({
          severity: 'warn',
          summary: 'No dry run',
          detail: 'Run a dry run first to preview the migration.',
          life: 3000,
        });
        return;
      }
      this.$confirm.require({
        group: 'featureMigrate',
        message:
          'Apply features cleanup for this tenant? This updates or deletes existing feature documents (duplicate legacy types are removed).',
        header: 'Confirm features migration',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
          if (this.featureMigrateAcceptHandled) return;
          this.featureMigrateAcceptHandled = true;
          this.runFeaturesMigrate()
            .then(() => {
              this.$toast.add({
                severity: 'success',
                summary: 'Migration completed',
                detail: 'Features collection cleanup finished.',
                life: 5000,
              });
            })
            .catch((error) => {
              this.$toast.add({
                severity: 'error',
                summary: 'Migration failed',
                detail: error?.message || 'Failed to run features migration',
                life: 5000,
              });
            })
            .finally(() => {
              this.featureMigrateAcceptHandled = false;
            });
        },
      });
    },
    clearFeatureMigrateResultsHandler() {
      this.clearFeatureMigrateResults();
      this.$toast.add({
        severity: 'info',
        summary: 'Results cleared',
        detail: 'Features migration results have been cleared',
        life: 3000,
      });
    },
    async handleDevTipSchedulePreview() {
      this.devTipSchedulePreviewLoading = true;
      this.devTipSchedulePreviewError = null;
      this.devTipSchedulePreview = null;
      this.devTipSchedulePreviewUserId = '';
      try {
        const response = await migrationService.getDevTipSchedulePreview();
        const data = response?.data ?? response;
        this.devTipSchedulePreview = data;
        this.$toast.add({
          severity: 'success',
          summary: 'Preview loaded',
          detail: 'Dev tip schedule preview loaded. Select a user to see dimensions.',
          life: 3000,
        });
      } catch (error) {
        this.devTipSchedulePreviewError = error?.response?.data?.message || error?.message || 'Failed to load preview';
        this.$toast.add({
          severity: 'error',
          summary: 'Preview failed',
          detail: this.devTipSchedulePreviewError,
          life: 5000,
        });
      } finally {
        this.devTipSchedulePreviewLoading = false;
      }
    },
    async handleDevTipPreviewUserChange() {
      const userId = this.devTipSchedulePreviewUserId || null;
      if (!userId) {
        this.handleDevTipSchedulePreview();
        return;
      }
      this.devTipSchedulePreviewLoading = true;
      this.devTipSchedulePreviewError = null;
      try {
        const response = await migrationService.getDevTipSchedulePreview(userId);
        const data = response?.data ?? response;
        this.devTipSchedulePreview = data;
      } catch (error) {
        this.devTipSchedulePreviewError = error?.response?.data?.message || error?.message || 'Failed to load preview for user';
      } finally {
        this.devTipSchedulePreviewLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.migrations-tab {
  max-width: 1200px;
}

code {
  font-family: 'Courier New', monospace;
  font-size: 0.9em;
}
</style>
